(() => {
  const root = document.getElementById('sowelu-crystal-guide');
  if (!root || root.dataset.playbackV7Initialized === 'true') return;
  root.dataset.playbackV7Initialized = 'true';

  const form = root.querySelector('[data-guide-form]');
  const chatEndpoint = String(root.dataset.endpoint || '').trim();
  const speechEndpoint = chatEndpoint.replace(/\/chat\/?$/i, '/speech');

  let activeAudio = null;
  let activeButton = null;
  let activeObjectUrl = null;
  let activeFetch = null;
  const audioCache = new WeakMap();

  function speakerMarkup() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 9v6h4l5 4V5l-5 4H6Z" fill="currentColor"/><path d="M18.2 8.2a5.4 5.4 0 0 1 0 7.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  }

  function pauseMarkup() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="6" width="3.5" height="12" rx="1" fill="currentColor"/><rect x="13.5" y="6" width="3.5" height="12" rx="1" fill="currentColor"/></svg>';
  }

  function resumeMarkup() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.8v12.4L18 12 8 5.8Z" fill="currentColor"/></svg>';
  }

  function setUi(button, state) {
    if (!button) return;
    button.classList.toggle('is-loading', state === 'loading');
    button.classList.toggle('is-playing', state === 'playing');
    button.classList.toggle('is-paused', state === 'paused');
    button.classList.toggle('is-error', state === 'error');
    button.setAttribute('aria-pressed', state === 'playing' ? 'true' : 'false');

    if (state === 'playing') {
      button.innerHTML = pauseMarkup();
      button.setAttribute('aria-label', 'Pause reading response');
      button.title = 'Pause';
    } else if (state === 'paused') {
      button.innerHTML = resumeMarkup();
      button.setAttribute('aria-label', 'Resume reading response');
      button.title = 'Resume';
    } else if (state === 'loading') {
      button.innerHTML = speakerMarkup();
      button.setAttribute('aria-label', 'Preparing voice playback');
      button.title = 'Preparing voice…';
    } else if (state === 'error') {
      button.innerHTML = speakerMarkup();
      button.setAttribute('aria-label', 'Voice playback unavailable');
      button.title = 'Voice playback unavailable — try again';
    } else {
      button.innerHTML = speakerMarkup();
      button.setAttribute('aria-label', 'Play response aloud');
      button.title = 'Play response';
    }
  }

  function responseText(button) {
    const row = button.closest('.sowelu-guide__message--assistant');
    const content = row?.querySelector('.sowelu-guide__bubble-content') || row?.querySelector('.sowelu-guide__bubble');
    return String(content?.innerText || '')
      .replace(/https:\/\/\S+/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 3900);
  }

  function releaseAudio() {
    if (activeAudio) {
      try {
        activeAudio.onended = null;
        activeAudio.onerror = null;
        activeAudio.pause();
        activeAudio.src = '';
      } catch (_) {}
    }
    activeAudio = null;
    if (activeObjectUrl) URL.revokeObjectURL(activeObjectUrl);
    activeObjectUrl = null;
  }

  function stopPlayback() {
    if (activeFetch) {
      try { activeFetch.abort(); } catch (_) {}
    }
    activeFetch = null;
    releaseAudio();
    if (activeButton) setUi(activeButton, 'idle');
    activeButton = null;
  }

  function pausePlayback() {
    if (!activeAudio || !activeButton || activeAudio.ended) return;
    activeAudio.pause();
    setUi(activeButton, 'paused');
  }

  async function resumePlayback() {
    if (!activeAudio || !activeButton || activeAudio.ended) return;
    const button = activeButton;
    try {
      await activeAudio.play();
      if (activeButton === button) setUi(button, 'playing');
    } catch (_) {
      if (activeButton === button) {
        releaseAudio();
        setUi(button, 'error');
        activeButton = null;
      }
    }
  }

  async function beginPlayback(button) {
    if (!button || !speechEndpoint || !/^https:\/\//i.test(speechEndpoint)) return;

    if (activeButton === button) {
      if (activeFetch) {
        stopPlayback();
        return;
      }
      if (activeAudio && !activeAudio.ended) {
        if (activeAudio.paused) await resumePlayback();
        else pausePlayback();
        return;
      }
    }

    stopPlayback();
    activeButton = button;
    setUi(button, 'loading');

    try {
      let blob = audioCache.get(button);
      if (!blob) {
        const text = responseText(button);
        if (!text) throw new Error('No speakable response text');

        const controller = new AbortController();
        activeFetch = controller;
        const response = await fetch(speechEndpoint, {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
          signal: controller.signal
        });
        if (activeFetch === controller) activeFetch = null;
        if (!response.ok) throw new Error(`Voice request failed: ${response.status}`);
        blob = await response.blob();
        if (!blob.size) throw new Error('Voice response was empty');
        audioCache.set(button, blob);
      }

      if (activeButton !== button) return;

      activeObjectUrl = URL.createObjectURL(blob);
      const audio = new Audio(activeObjectUrl);
      activeAudio = audio;
      audio.preload = 'auto';
      audio.onended = () => {
        if (activeButton === button) stopPlayback();
      };
      audio.onerror = () => {
        if (activeButton === button) {
          releaseAudio();
          setUi(button, 'error');
          activeButton = null;
        }
      };

      await audio.play();
      if (activeButton === button) setUi(button, 'playing');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (activeButton === button) {
        releaseAudio();
        setUi(button, 'error');
        activeButton = null;
      }
    }
  }

  // Register before Voice V5 so this small adapter owns playback clicks while
  // V5 continues to own microphone/transcription state.
  document.addEventListener('click', (event) => {
    const playback = event.target.closest('#sowelu-crystal-guide .sowelu-guide__playback');
    if (playback) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      beginPlayback(playback);
      return;
    }

    if (event.target.closest('#sowelu-crystal-guide [data-guide-mic], #sowelu-crystal-guide [data-guide-close], #sowelu-crystal-guide [data-guide-reset]')) {
      stopPlayback();
    }
  }, true);

  form?.addEventListener('submit', () => stopPlayback(), true);
  window.addEventListener('beforeunload', () => stopPlayback());
})();
